import Button from 'features/common/components/Button'
import photoPlaceholder from 'assets/photo-placeholder.svg'
import Input from 'features/common/components/Input'
import EmployeeModal from '../common/EmployeeModal'
import { Employee } from '../../../../../common/core/entities/employee'
import { useTranslation } from 'react-i18next'
import { useTypedTranslation } from 'i18n'
import useEmployeeForm from '../../../../../common/hooks/useEmployeeForm'

interface StaffModalProps {
    initialData: Employee | null
    onClose: () => void
    onDelete: (e: Employee) => void
}

const roles = [
    'Administator',
    'Cook',
    'Waiter',
    'Bartender',
    'Courier',
    'Barber',
    'Master',
    'Barista',
    'Other',
]

const StaffModal = ({ initialData, onClose, onDelete }: StaffModalProps) => {
    const { t } = useTranslation()
    const {
        formData,
        handlers: { onChange, onSubmit },
        errors,
        disableSubmit,
    } = useEmployeeForm({ initialData, onSuccess: onClose })

    const onChangeForm =
        (prop: keyof typeof formData) =>
            (value: (typeof formData)[keyof typeof formData]) => {
                onChange({ prop, value })
            }
    const onChangeInput =
        (prop: keyof typeof formData) =>
            (e: React.ChangeEvent<HTMLInputElement>) => {
                onChange({ prop, value: e.target.value })
            }

    const onChangeFileInput = async (
        e: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const files = e.target.files
        if (!files) {
            return
        }
        const file = files[0]

        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = () => {
            const fileB64 = reader.result
            if (!fileB64) {
                return
            }
            onChange({ prop: 'avatar', value: fileB64 as string })
        }
    }

    const onChangeDateInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const birthInDateTime = e.target.value + 'T00:00:00.000Z'
        return onChange({ prop: 'birth', value: birthInDateTime })
    }

    const onChangeRole = (e: React.ChangeEvent<HTMLSelectElement>) =>
        onChangeForm('role')(e.target.value)

    const onSubmitWrapper = (e: React.FormEvent) => {
        e.preventDefault()
        onSubmit()
    }

    const onClickDelete = () => {
        if (!initialData) {
            return
        }
        onClose()
        onDelete(initialData)
    }

    const birthDateWithoutTime = formData.birth
        ? formData.birth.slice(0, 10)
        : null
    return (
        <EmployeeModal isOpen={true} onClose={onClose}>
            <form
                onSubmit={onSubmitWrapper}
                className="flex flex-col items-stretch justify-between gap-4"
            >
                <div className="flex h-20 w-20 self-center">
                    <label htmlFor="avatar-input">
                        <img
                            className="h-20 w-20 cursor-pointer rounded-full"
                            src={formData.avatar || photoPlaceholder}
                            alt="place"
                        />
                    </label>
                    <input
                        hidden
                        onChange={onChangeFileInput}
                        id="avatar-input"
                        type="file"
                        accept="image/*"
                    />
                </div>
                <div className="flex flex-col gap-1">
                    <div className="pl-5 text-left text-sm">
                        {t('QR_EMPLOYEE_MODAL_FULL_NAME')}
                    </div>
                    <FormInput
                        value={formData.firstName}
                        onChange={onChangeInput('firstName')}
                        placeholder={t('QR_EMPLOYEE_MODAL_FULL_NAME_PLACEHOLDER')}

                        errors={errors.firstName}
                    />
                </div>

                <div className="flex flex-col gap-1">
                    <div className="pl-5 text-left text-sm">
                        {t('QR_EMPLOYEE_MODAL_DATE_OF_BIRTH')}
                    </div>
                    <FormInput
                        className="bg-white text-left"
                        value={birthDateWithoutTime || ''}
                        type="date"
                        onChange={onChangeDateInput}
                        placeholder={t('QR_EMPLOYEE_MODAL_DATE_OF_BIRTH_PLACEHOLDER')}
                        errors={errors.birth}
                    />
                </div>

                <div className="flex flex-col gap-1">
                    <div className="pl-5 text-left text-sm">
                        {t('QR_EMPLOYEE_MODAL_PHONE')}
                    </div>
                    <FormInput
                        value={formData.phone}
                        onChange={onChangeInput('phone')}
                        placeholder={t('QR_EMPLOYEE_MODAL_PHONE_PLACEHOLDER')}
                        type="tel"
                        errors={errors.phone}
                    />
                </div>

                <div className="flex flex-col gap-1">
                    <div className="pl-5 text-left text-sm">
                        {t('QR_EMPLOYEE_MODAL_EMAIL')}
                    </div>
                    <FormInput
                        value={formData.email}
                        onChange={onChangeInput('email')}
                        placeholder={t('QR_EMPLOYEE_MODAL_EMAIL_PLACEHOLDER')}
                        type="email"
                        errors={errors.email}
                    />
                </div>
                <div className="flex flex-col gap-1">
                    <div className="pl-5 text-left text-sm">
                        {t('QR_EMPLOYEE_MODAL_POSITION')}
                    </div>
                    <div className="relative w-full">
                        <select
                            value={formData.role}
                            className={`h-9 w-full appearance-none rounded-full border border-grey-70 bg-white px-5 ${formData.role === ''
                                ? 'text-grey-20'
                                : 'text-black'
                                } min-w-0 text-sm active:border-black`}
                            placeholder={t('QR_EMPLOYEE_MODAL_ROLE_PLACEHOLDER')}
                            onChange={onChangeRole}
                        >
                            <option value="" disabled>
                                {t('QR_EMPLOYEE_MODAL_ROLE_PLACEHOLDER')}                            </option>
                            {roles.map((r) => (
                                <option key={r} value={r}>
                                    {r}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
                {initialData ? (
                    <div className="flex justify-end">
                        <button
                            className="text-yellow-40"
                            type="button"
                            onClick={onClickDelete}
                        >
                            {t('QR_EMPLOYEE_MODAL_REMOVE_EMPLOYEE')}
                        </button>
                    </div>
                ) : null}
                <div className="flex items-center justify-center gap-2">
                    <div className="flex h-10 w-30">
                        <Button view={'alt'}>{t('QR_EMPLOYEE_MODAL_CANCEL_BUTTON')}</Button>
                    </div>
                    <div className="flex h-10 w-30">
                        <Button disabled={disableSubmit} type="submit">
                            {t('QR_EMPLOYEE_MODAL_SAVE_BUTTON')}
                        </Button>
                    </div>
                </div>
            </form>
        </EmployeeModal>
    )
}

type FormInputProps = React.ComponentProps<typeof Input> & {
    errors?: string[]
}

const FormInput = ({ errors, ...props }: FormInputProps) => {
    const { t } = useTypedTranslation()
    return (
        <div className="relative flex flex-1">
            <Input
                {...props}
                className={`h-9 w-full min-w-0 flex-1 border ${props.className || ''
                    }`}
            />
            <div className="absolute top-full">
                {errors ? (
                    <div className="pl-5 text-xs text-red-700">
                        {t(errors[0])}
                    </div>
                ) : null}
            </div>
        </div>
    )
}

export default StaffModal
